import { useOutletContext } from 'react-router-dom'

import {
  ImputableProjectForSelectFragment,
  MyProjectsScheduleEmployeeFragment,
  MyProjectsScheduleProjectWorkerFragment,
  MyScheduleDocument,
  MyScheduleQuery,
  ProjectForSelectFragment,
  ProjectPlannedTimeFragment,
} from 'generated/resources/graphql'
import moment, { Moment } from 'moment'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import { ScheduleParseDataType } from 'modules/project_management/components/Schedule/Body'
import { ScheduleRecordType } from 'modules/project_management/components/Schedule/Body/ScheduleItem/types'
import { FooterElement } from 'modules/project_management/components/Schedule/Footer/index'
import Schedule from 'modules/project_management/components/Schedule/index'
import { RouteContextParams } from 'modules/project_management/pages/MyProjectsRootPage'

const MySchedule = () => {
  useEffect(() => {
    track(trackingEvents.projectManagement.flexibleTracking.visitMyProjectsScheduleSection, {})
  }, [])

  const { employeeIdNumber } = useOutletContext<RouteContextParams>()

  const dataSource = (data: MyScheduleQuery) => {
    const connection = data?.projectManagement.projectWorkersConnection
    return {
      edges:
        connection?.edges.map((edge) => ({
          cursor: edge.cursor ?? '',
          node: edge.node,
        })) ?? [],
      pageInfo: {
        hasNextPage: connection?.pageInfo.hasNextPage ?? false,
        endCursor: connection?.pageInfo.endCursor ?? '',
      },
    }
  }

  const projectToSelectFragment = (
    imputableProject: ImputableProjectForSelectFragment
  ): ProjectForSelectFragment => {
    return {
      id: imputableProject.id,
      name: imputableProject.name,
      code: imputableProject.code,
      subprojectsConnection: imputableProject.subprojectsConnection,
    }
  }

  const generatePlannedTimes = (
    plannedTimes: ProjectPlannedTimeFragment[],
    startDate: Moment,
    endDate: Moment
  ) => {
    if (plannedTimes.length === 0) return []

    const getDatesArray = () => {
      const dates = []
      const current = moment(startDate)

      while (current.isSameOrBefore(endDate, 'day')) {
        dates.push(current.clone())
        current.add(1, 'day')
      }

      return dates
    }

    const dateRange = getDatesArray()

    const totalByDays = dateRange.map((day) => {
      return plannedTimes.reduce((acc, plannedTime) => {
        if (
          moment(plannedTime.startDate).isSameOrBefore(day, 'day') &&
          moment(plannedTime.endDate).isSameOrAfter(day, 'day') &&
          plannedTime.weekDays.includes(day.weekday())
        ) {
          return acc + plannedTime.dailyMinutes
        }
        return acc
      }, 0)
    })

    const groupConsecutive = (arr: number[]) => {
      const result = []
      let current = { total: arr[0] || 0, count: 1 }

      for (let i = 1; i < arr.length; i++) {
        if (arr[i] === current.total) {
          current.count++
        } else {
          result.push(current)
          current = { total: arr[i] || 0, count: 1 }
        }
      }

      result.push(current)
      return result
    }

    const groupedTotalByDays = groupConsecutive(totalByDays)

    const generateBlocksFromGroups = (
      arrGroups: { total: number; count: number }[]
    ): ScheduleRecordType => {
      const blocks = []
      const current = moment(startDate)

      for (const group of arrGroups) {
        const { total, count } = group

        if (total !== 0) {
          const blockStart = current.clone()
          const blockEnd = current.clone().add(count - 1, 'days')

          blocks.push({
            id: undefined,
            group: i18n.t('project_management.schedule.other_assignments.title'),
            title: null,
            subtitle: null,
            projectWorkerId: undefined,
            project: undefined,
            employee: undefined,
            startDate: blockStart.format('YYYY-MM-DD'),
            endDate: blockEnd.format('YYYY-MM-DD'),
            realStartDate: blockStart.format('YYYY-MM-DD'),
            realEndDate: blockEnd.format('YYYY-MM-DD'),
            dailyMinutes: total,
          })
        }

        current.add(count, 'days')
      }

      return { hidden: blocks }
    }

    return [
      {
        firstColumn: {
          id: '',
          title: i18n.t('project_management.schedule.other_assignments.title'),
          subtitle: '',
        },
        records: generateBlocksFromGroups(groupedTotalByDays),
        otherAssignments: true,
      },
    ]
  }

  const parseData = (
    data: MyScheduleQuery | undefined,
    startDate: Moment,
    endDate: Moment
  ): ScheduleParseDataType => {
    const transformRecords = (
      projectWorker: MyProjectsScheduleProjectWorkerFragment,
      employee: EmployeeForSelectFragmentNoAccess
    ): ScheduleRecordType => {
      return projectWorker.plannedRecords.reduce<ScheduleRecordType>((acc, record) => {
        const key = record.subproject?.id ? record.subproject?.id?.toString() : 'null'

        if (!acc[key]) {
          acc[key] = []
        }

        if (
          projectWorker.imputableProject &&
          moment(record.startDate).isSameOrBefore(endDate) &&
          moment(record.endDate).isSameOrAfter(startDate)
        ) {
          acc[key].push({
            id: record.id,
            startDate: record.startDate,
            endDate: record.endDate,
            realStartDate: record.startDate,
            realEndDate: record.endDate,
            dailyMinutes: record.dailyMinutes,
            group: projectWorker.imputableProject?.name ?? '',
            title: record.subproject ? record.subproject.name : null,
            subtitle: contactFullName(projectWorker.imputableProject?.client),
            projectWorkerId: projectWorker.id,
            project: projectToSelectFragment(projectWorker.imputableProject),
            subproject: record.subproject ?? undefined,
            employee,
            weekDays: record.weekDays,
          })
        }

        if (acc[key].length === 0) {
          delete acc[key]
        }

        return acc
      }, {})
    }

    if (!data?.employees.employee) return { items: [], totalCount: 0 }

    const employee = data.employees.employee

    const employeeToSelectFragment = (
      employeeSelect: MyProjectsScheduleEmployeeFragment
    ): EmployeeForSelectFragmentNoAccess => {
      return {
        id: employeeSelect.id,
        fullName: employeeSelect.fullName,
        firstName: employeeSelect.firstName,
        lastName: employeeSelect.lastName,
        access: { avatar: null },
      }
    }

    const employeeToSelect = employeeToSelectFragment(employee)

    const projectWorkers =
      data.projectManagement.projectWorkersConnection.edges.map((edge) => edge.node) || []

    const estimatedWorkTimes = data.projectManagement.projectContributor?.estimatedWorkTimes || []
    const plannedTimes = data.projectManagement.projectContributor?.plannedTimes || []

    const estimatedWorkTimesData = estimatedWorkTimes.reduce((acc, estimatedWorkTime) => {
      const expectedMinutes = Math.max(estimatedWorkTime.expectedMinutes, 0)
      const minutes = estimatedWorkTime.minutes
      const hasDayOff = estimatedWorkTime.offDays.some((offDay) => !offDay.workable)
      const minutesOff = hasDayOff ? Math.max(expectedMinutes - minutes, 0) : 0

      return {
        ...acc,
        [estimatedWorkTime.date]: {
          expectedMinutes: expectedMinutes,
          minutes: minutes,
          minutesOff: minutesOff,
        },
      }
    }, {})

    return {
      items: [
        {
          headerRow: {
            id: parseInt(employee.id.toString() || '0'),
            fullName: employee.fullName || '',
            avatar: employee.avatar?.url || '',
            jobTitle: employee.referenceContract?.jobTitle || '',
            estimatedWorkTimesData: estimatedWorkTimesData,
          },
          employeeToSelect,
          rows: projectWorkers
            .map((pw: MyProjectsScheduleProjectWorkerFragment) => {
              const records = transformRecords(pw, employeeToSelect)
              if (Object.keys(records).length === 0) return null

              return {
                firstColumn: {
                  id: pw.imputableProject?.id.toString() || '',
                  title: pw.imputableProject?.name || '',
                  subtitle: contactFullName(pw.imputableProject?.client),
                },
                records,
              }
            })
            .filter((row) => row !== null)
            .concat(generatePlannedTimes(plannedTimes, startDate, endDate)),
        },
      ],
      totalCount: 0,
    }
  }

  const footerElementsList: FooterElement[] = [
    {
      color: 'champagne400',
      circleType: 'circle',
      description: i18n.t('project_management.schedule_contributors.footer_legend.time_off'),
    },
    {
      color: 'purple200',
      circleType: 'circle',
      description: i18n.t(
        'project_management.schedule_contributors.footer_legend.total_assigned_hours'
      ),
    },
    {
      color: 'purple1000',
      circleType: 'circle-line',
      description: i18n.t(
        'project_management.schedule_contributors.footer_legend.detailed_hours_per_project'
      ),
    },
  ]

  return (
    employeeIdNumber && (
      <Schedule
        document={MyScheduleDocument}
        documentVariables={{
          employeeId: employeeIdNumber,
          startDate: moment().startOf('month').format('YYYY-MM-DD'),
          endDate: moment().endOf('month').format('YYYY-MM-DD'),
        }}
        dataSource={dataSource}
        parseData={parseData}
        footerLegend={footerElementsList}
        isMyProjectsView
      />
    )
  )
}

export default MySchedule
